import { v4 as uuidv4 } from 'uuid';
import { enqueueSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import {
  AnalyticsModules,
  SalesEnablementActionType,
  useAnalytics,
  useMixpanel,
  useSubscribedMutation
} from '@setvi/shared/hooks';
import { useProductMetadata } from 'pages/products/hooks/use-product-metadata';
import { useSalesKitStore } from 'componentsV2/product/details/store';

import {
  getSalesEnablementContentQuery,
  SalesEnablementQueryKey
} from 'Services/Query';
import {
  generateSalesEnablementMutation,
  getSalesEnablementProductPromptsMutation,
  toggleSalesEnablementMutation
} from 'Services/Mutations';
import { SalesEnablementProps } from '..';

export interface ContentProps {
  id: string;
  name: string;
  content: string;
}

export interface SalesEnablementItemProps {
  _id: string;
  name: string;
  order: number;
  isFAQ: boolean;
  content: ContentProps[];
  loading: boolean;
}

export const useSalesEnablement = ({ product }: SalesEnablementProps) => {
  const { track } = useMixpanel();
  const { captureAnalytics } = useAnalytics();

  const {
    error: metadataError,
    isLoading: isMetadataLoading,
    metadataFetched,
    metadataParams,
    errorCode: metadataErrorCode,
    handleMetadata
  } = useProductMetadata({
    product
  });

  const [items, setItems] = useState<SalesEnablementItemProps[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<string>(null);
  const [activeItem, setActiveItem] = useState('');

  // Create array of 12 skeleton items for loading state
  const skeletonItems = Array.from({ length: 12 }, (_, index) => ({
    _id: `skeleton-${index}`,
    name: '',
    order: index,
    isFAQ: false,
    content: [] as ContentProps[],
    loading: true
  }));

  // if its root category, use 1 as default
  const productCategoryId = useMemo(
    () => product?.categories?.[0]?._id || '1',
    [product]
  );

  const { setSalesKit, salesKit } = useSalesKitStore();

  const { data, isFetching, error } = useQuery({
    ...getSalesEnablementContentQuery(
      product?._id,
      product?.categories?.[0]?._id
    ),
    enabled: !!product?._id,
    retry: false
  });

  const {
    mutateAsync: getPrompts,
    isLoading: arePromptsLoading,
    error: promptsErrpr
  } = useMutation(getSalesEnablementProductPromptsMutation(productCategoryId));

  const { mutateAsync: toggleSalesEnablement, isLoading: isToggleLoading } =
    useSubscribedMutation(toggleSalesEnablementMutation(), [
      SalesEnablementQueryKey.GET_CONTENT,
      SalesEnablementQueryKey.GET_SINGLE_CONTENT_BY_PROMPT_NAME
    ]);

  const { mutateAsync: generateSalesEnablement, error: salesError } =
    useMutation({
      ...generateSalesEnablementMutation(),
      onError: () => {}
    });

  const errorCode = useMemo(() => {
    const err: any = promptsErrpr || error || salesError;

    return err?.status || metadataErrorCode;
  }, [metadataErrorCode, promptsErrpr, error, salesError]);

  const generateContent = async (id: string, metadata: string) => {
    const res = await generateSalesEnablement({
      promptId: id,
      _id: product?._id,
      metadata
    });

    let contentData: ContentProps[] = [];
    if (res) {
      try {
        contentData =
          typeof res?.data === 'string'
            ? JSON.parse(
                res?.data?.map((i: ContentProps) => ({ ...i, id: uuidv4() }))
              )
            : res?.data;
      } catch (
        e
        // eslint-disable-next-line no-empty
      ) {}
    }

    setItems(prev =>
      prev?.map(item =>
        item._id === id
          ? {
              ...item,
              content: contentData,
              isFAQ: res?.isFAQ || false,
              loading: false
            }
          : item
      )
    );
  };

  const handleItems = async () => {
    if (!loading) setLoading(true);
    const res = await getPrompts();

    const savedPrompts = data?.data?.map(
      ({ promptId }: { promptId: string }) => promptId
    );

    const missingPrompts = res?.filter(
      ({ _id }: { _id: string }) => !savedPrompts?.includes(_id)
    );

    const metadata = await handleMetadata();

    if (!metadata) {
      setLoading(false);
      return;
    }

    if (missingPrompts?.length) {
      const tItems = missingPrompts?.map(
        (item: Partial<SalesEnablementItemProps>) => ({
          _id: item._id,
          name: item.name,
          order: item.order,
          loading: true
        })
      );

      tItems?.forEach(({ _id }: { _id: string }) => {
        generateContent(_id, metadata);
      });

      setItems(prev => [...prev, ...tItems]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);

    const valid =
      !!metadataParams?.manufacturerName && !!metadataParams?.manufacturerSku;
    const isDisabled = data?.disabled;

    if (!isFetching && !!data && !isDisabled) {
      const formatedItems = data?.data?.map((item: any) => ({
        _id: item?.prompt?._id,
        name: item?.prompt?.name,
        order: item?.prompt?.order,
        content:
          item?.data?.map((i: ContentProps) => ({ ...i, id: uuidv4() })) || [],
        isFAQ: item?.prompt?.isFAQ || false,
        loading: false
      }));

      setItems(formatedItems);

      if (data?.itemTotal !== data?.promptTotal && valid) {
        handleItems();
      } else {
        setLoading(false);
      }
    }

    if (!isFetching && !data && valid && !isDisabled) {
      handleItems();
    }

    if (!isFetching && isDisabled) {
      setItems([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isFetching]);

  const handleRegeneration = async (promptId: string) => {
    const active = items?.find(item => item._id === promptId);
    if (!active) return;

    setItems(prev =>
      prev?.map(item =>
        item._id === active?._id
          ? {
              ...item,
              loading: true
            }
          : item
      )
    );

    const metadata = await handleMetadata();
    if (!metadata) return;

    generateContent(active?._id, metadata);
  };

  const regenerateAll = async (forceRefresh = false) => {
    const metadata = await handleMetadata(forceRefresh);
    if (!metadata) return;

    setItems(prev =>
      prev?.map(item => ({
        ...item,
        loading: true
      }))
    );

    items?.forEach(({ _id }: { _id: string }) => {
      generateContent(_id, metadata);
    });
  };

  const initalLoading = useMemo(() => {
    if (isFetching || arePromptsLoading || loading || isMetadataLoading)
      return true;
    if (!items?.length) return false;

    const alreadyDoneItems = items?.some(item => !item?.loading);

    return !data?.data?.length ? items?.[0]?.loading : !alreadyDoneItems;
  }, [data, isFetching, loading, arePromptsLoading, isMetadataLoading, items]);

  useEffect(() => {
    if (!!activeItem && !!items?.length) {
      const activeTab = items?.find(item => item._id === activeItem);
      const newExpandedAccordion = activeTab?.content?.[0]?.name;

      setExpanded(newExpandedAccordion);

      track({
        name: 'Sales Enablement',
        props: {
          tab: activeTab?.name || '',
          productId: product?._id
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItem]);

  useEffect(() => {
    track({
      name: 'Sales Enablement',
      props: { productId: product?._id }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    captureAnalytics(
      {
        productSKU: product.sku,
        actionType: SalesEnablementActionType.View
      },
      AnalyticsModules.SalesEnablement
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const err: any = promptsErrpr || error || salesError;

    if ((metadataError as any)?.message || err?.message)
      captureAnalytics(
        {
          error: metadataError ? (metadataError as any)?.message : err?.message,
          productSKU: product.sku,
          actionType: metadataError
            ? SalesEnablementActionType.MetadataError
            : SalesEnablementActionType.Error
        },
        AnalyticsModules.SalesEnablement
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metadataError, promptsErrpr, error, salesError]);

  const handleItemClick = (item: string) => {
    setExpanded(null);
    setActiveItem(item);

    const currItem = items.find(i => i?._id === item);

    captureAnalytics(
      {
        productSKU: product.sku,
        item: currItem?.name || '',
        content: currItem?.content?.[0]?.name || '',
        actionType: SalesEnablementActionType.ItemClick
      },
      AnalyticsModules.SalesEnablement
    );
  };

  const handleContentClick = (name: string, itemName: string) => {
    setExpanded(prev => (prev === name ? null : name));

    captureAnalytics(
      {
        productSKU: product.sku,
        item: itemName || '',
        content: name || '',
        actionType: SalesEnablementActionType.ContentClick
      },
      AnalyticsModules.SalesEnablement
    );
  };

  const handleToggle = async () => {
    if (!product?._id) return;
    const response = await toggleSalesEnablement(product?._id || '');

    if (response?.error) {
      return;
    }

    enqueueSnackbar(
      `Sales Enablement ${data?.disabled ? 'enabled' : 'disabled'} successfully`,
      {
        variant: 'success'
      }
    );
  };

  useEffect(() => {
    if (items?.length) {
      if (!initalLoading && !activeItem?.trim()) {
        const firstActiveItem = items?.find(
          item => !item?.loading && item?.content?.length
        );
        setActiveItem(firstActiveItem?._id || '');
      }

      const aiOverview = items?.find(item => item?.name === 'AI Overview');
      setSalesKit({
        _id: salesKit?._id || aiOverview?._id || '',
        promptId: salesKit?.promptId || '',
        productId: product?._id || salesKit?.productId || '',
        disabled: salesKit?.disabled || false,
        data: aiOverview?.content || [],
        prompt: salesKit?.prompt || {}
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, initalLoading]);

  return {
    items,
    skeletonItems,
    errorCode,
    activeItem,
    metadataFetched,
    initalLoading,
    expanded,
    isFetching,
    isDisabled: data?.disabled || false,
    isToggleLoading,
    metadataErrorCode,
    setExpanded,
    regenerateAll,
    setActiveItem,
    handleItemClick,
    handleToggle,
    handleContentClick,
    handleRegeneration
  };
};
